import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge, Button, MenuContent, MenuItem, MenuTrigger, Spinner } from '@atlas/ui-primitives'
import { MoreHorizontal } from 'lucide-react'
import { jobFunctionLabel } from '@/lib/job-functions'
import { roleLabel } from '@/lib/roles'
import type { UserDTO } from '@/services/user.service'

// Presentational user directory (feature-local, prop-driven → storiable). Renders
// ONLY approved directory fields — display name, email, role, job function, active
// status. It never receives or shows cognitoSub, tokens, passwords, groups, or
// audit internals.

export interface UserDirectoryProps {
	users: UserDTO[]
	isLoading?: boolean
	isError?: boolean
	onEditRole: (user: UserDTO) => void
	onEditJobFunction: (user: UserDTO) => void
	onToggleStatus: (user: UserDTO) => void
}

/**
 * `isActive` is absent when the directory was read below `ADMIN` (ADR-0031).
 * This page is SUPER_ADMIN-only so that does not happen here, but the badge
 * says so rather than guessing a state it was not told.
 */
function StatusBadge({ isActive }: { isActive: boolean | undefined }) {
	if (isActive === undefined) return <span className="text-ds-text-muted">—</span>
	return (
		<Badge variant={isActive ? 'primary' : 'secondary'} size="sm">
			{isActive ? 'Active' : 'Inactive'}
		</Badge>
	)
}

export function UserDirectory({
	users,
	isLoading,
	isError,
	onEditRole,
	onEditJobFunction,
	onToggleStatus,
}: UserDirectoryProps) {
	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12" aria-busy="true">
				<Spinner size="lg" label="Loading users…" />
			</div>
		)
	}

	if (isError) {
		return (
			<p role="alert" className="py-8 text-center text-sm text-ds-danger">
				Could not load users. Please try again.
			</p>
		)
	}

	if (users.length === 0) {
		return <EmptyState title="No users yet." />
	}

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Name</TableHead>
					<TableHead>Email</TableHead>
					<TableHead>Role</TableHead>
					<TableHead>Job function</TableHead>
					<TableHead>Status</TableHead>
					<TableHead>Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{users.map((user) => (
					<TableRow key={user.userId}>
						<TableCell className="font-medium">{user.displayName}</TableCell>
						<TableCell className="text-ds-text-muted">{user.email}</TableCell>
						<TableCell>
							<Badge variant="muted" size="sm">
								{roleLabel(user.role)}
							</Badge>
						</TableCell>
						<TableCell>
							<Badge variant="secondary" size="sm">
								{jobFunctionLabel(user.jobFunction)}
							</Badge>
						</TableCell>
						<TableCell>
							<StatusBadge isActive={user.isActive} />
						</TableCell>
						<TableCell>
							{/* A menu, not a row of buttons: three full-text actions already
							    overflowed the row, and every action added later would cost
							    another slice of the table's width. */}
							<MenuTrigger>
								<Button variant="outline" size="sm" aria-label={`Actions for ${user.displayName}`}>
									<MoreHorizontal className="h-4 w-4" aria-hidden="true" />
								</Button>
								<MenuContent placement="bottom end">
									<MenuItem onAction={() => onEditRole(user)}>Change role</MenuItem>
									<MenuItem onAction={() => onEditJobFunction(user)}>Change job function</MenuItem>
									<MenuItem onAction={() => onToggleStatus(user)}>{user.isActive ? 'Deactivate' : 'Activate'}</MenuItem>
								</MenuContent>
							</MenuTrigger>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}
